const rstData = [
  { code: "1", meaning: "Unreadable", type: "readability" },
  { code: "2", meaning: "Barely readable, occasional words distinguishable", type: "readability" },
  { code: "3", meaning: "Readable with considerable difficulty", type: "readability" },
  { code: "4", meaning: "Readable with practically no difficulty", type: "readability" },
  { code: "5", meaning: "Perfectly readable", type: "readability" },
  { code: "1", meaning: "Faint signals, barely perceptible", type: "signal_strength" },
  { code: "2", meaning: "Very weak signals", type: "signal_strength" },
  { code: "3", meaning: "Weak signals", type: "signal_strength" },
  { code: "4", meaning: "Fair signals", type: "signal_strength" },
  { code: "5", meaning: "Fairly good signals", type: "signal_strength" },
  { code: "6", meaning: "Good signals", type: "signal_strength" },
  { code: "7", meaning: "Moderately strong signals", type: "signal_strength" },
  { code: "8", meaning: "Strong signals", type: "signal_strength" },
  { code: "9", meaning: "Extremely strong signals", type: "signal_strength" }
];

// Helper function to generate wrong answers
const generateWrongAnswers = (correctAnswer, allAnswers, count = 3) => {
  const wrongAnswers = allAnswers
    .filter(answer => answer !== correctAnswer)
    .sort(() => Math.random() - 0.5)
    .slice(0, count);
  return wrongAnswers;
};

// Generate quiz questions from RST data
const generateRSTQuiz = () => {
  const readabilityCodes = rstData.filter(item => item.type === 'readability');
  const signalStrengthCodes = rstData.filter(item => item.type === 'signal_strength');
  const questions = [];
  let questionId = 1;

  // Generate questions for each code, keeping readability and signal strength separate
  rstData.forEach((item, index) => {
    // Type 1: Given code, what does it mean?
    const isReadability = item.type === 'readability';
    const relevantCodes = isReadability ? readabilityCodes : signalStrengthCodes;
    const wrongMeanings = generateWrongAnswers(item.meaning, relevantCodes.map(c => c.meaning));
    const meaningOptions = [item.meaning, ...wrongMeanings].sort(() => Math.random() - 0.5);
    const correctMeaningIndex = meaningOptions.indexOf(item.meaning);
    
    const codeType = isReadability ? 'readability' : 'signal strength';
    
    questions.push({
      id: questionId++,
      question: `What does ${item.code} mean for ${codeType}?`,
      options: meaningOptions,
      correct: correctMeaningIndex,
      explanation: `${item.code} means "${item.meaning}".`
    });

    // Type 2: Given meaning, what is the code? (create for key items)
    if (item.code === '1' || item.code === '5' || item.code === '9' || index % 4 === 0) {
      const wrongCodes = generateWrongAnswers(item.code, relevantCodes.map(c => c.code));
      const codeOptions = [item.code, ...wrongCodes].sort(() => Math.random() - 0.5);
      const correctCodeIndex = codeOptions.indexOf(item.code);
      
      questions.push({
        id: questionId++,
        question: `Which ${codeType} code represents: "${item.meaning}"?`,
        options: codeOptions.map(code => `${code}`),
        correct: correctCodeIndex,
        explanation: `${item.code} represents "${item.meaning}".`
      });
    }
  });

  // Add some composite RS questions (without tone)
  const compositeQuestions = [
    {
      id: questionId++,
      question: "What does an RS report of 59 mean?",
      options: [
        "Perfectly readable, extremely strong signals",
        "Readable with difficulty, fairly good signals",
        "Barely readable, weak signals",
        "Unreadable, faint signals"
      ],
      correct: 0,
      explanation: "RS 59 means: 5 (Perfectly readable) and 9 (Extremely strong signals). This is an excellent signal report commonly used in amateur radio."
    },
    {
      id: questionId++,
      question: "What does an RS report of 33 mean?",
      options: [
        "Readable with considerable difficulty, weak signals",
        "Perfectly readable, weak signals",
        "Readable with considerable difficulty, extremely strong signals",
        "Barely readable, weak signals"
      ],
      correct: 0,
      explanation: "RS 33 means: 3 (Readable with considerable difficulty) and 3 (Weak signals). This indicates a marginal signal that requires attention to copy."
    },
    {
      id: questionId++,
      question: "In amateur radio, what do the letters 'R' and 'S' stand for in signal reports?",
      options: [
        "Readability and Signal Strength",
        "Range and Speed",
        "Reception and Sound",
        "Radio and Station"
      ],
      correct: 0,
      explanation: "R stands for Readability (how well the signal can be understood) and S stands for Signal Strength (how strong the received signal is)."
    },
    {
      id: questionId++,
      question: "What is the range of values for readability codes?",
      options: [
        "1 to 5",
        "1 to 9",
        "0 to 5",
        "1 to 10"
      ],
      correct: 0,
      explanation: "Readability codes range from 1 (unreadable) to 5 (perfectly readable). There are only 5 levels for readability assessment."
    },
    {
      id: questionId++,
      question: "What is the range of values for signal strength codes?",
      options: [
        "1 to 9",
        "1 to 5",
        "0 to 9",
        "1 to 10"
      ],
      correct: 0,
      explanation: "Signal strength codes range from 1 (faint signals, barely perceptible) to 9 (extremely strong signals). There are 9 levels for signal strength assessment."
    }
  ];

  questions.push(...compositeQuestions);

  // Shuffle questions and limit to reasonable number
  return questions.sort(() => Math.random() - 0.5).slice(0, 18);
};

export const rstQuizData = {
  id: 'rst-quiz',
  title: 'RS System Quiz',
  subtitle: 'Master the RS signal reporting system',
  description: 'Test your knowledge of the RS (Readability, Signal Strength) reporting system',
  questions: generateRSTQuiz()
};

export default rstQuizData;